import {computed, observable, observe, isObservableProp, intercept, toJS, action} from 'mobx';
import bbox from "@turf/bbox";
import {getTrail} from "../sources/Trails";


window.bbox = bbox;

class VandAMapStore {
  @observable latlng = "";
  @observable position = observable.array([-3000, 3800]);
  @observable zoom = 2;
  @observable bounds = [[1568, 9048], [-7576, -1448]];

  @observable.ref mapToolsOpen = false;
  @observable.ref leafletRef; // assigned in the VandAMap component

  @observable.ref currentItem; // the current 'active item', when the user drills down through a list into a single item
  @observable.ref highlightedFeature;
  @observable.ref highlightId; // the map feature/item clicked on by the user - updates the currentHighlightItem prop
  @observable.ref relatedResources; // set when we're at a location so we can show other resources (like events) when the user clicks on a room

  @observable.ref trayContainerClass = ''; // for overriding the tray size class
  @observable.ref bounds;

  @observable userLocationId;
  @observable foundLocationId;
  @observable trailId = null;

  @observable findables = [];
  @observable events = [];
  @observable trails = [];
  @observable trailStops = [];

  @observable floorMarkerMessage = "";

  @observable floor = null;

  oldFloor = null;
  @observable floorGeojson = observable.map({});
  allFloorsData = [];

  ignoredProperties = [];

  @observable geoJsonRenderTime = new Date().toISOString();

  // some attributes for restoring to previous states
  _position = null;
  _zoom = null;
  _bounds = null;

  @computed get currentFloor() {
    return this.floorGeojson.get(this.floor);
  }

  @computed get geoJsonFeatures() {
    let features = [];
    window.toJS = toJS;
    this.floorGeojson.forEach((v,k) => { features.push(toJS(v.features)) });
    return [].concat(...features)

  }

  @computed get currentHighlightItem() {
    const highlightItem = this.allFloorsData.find((i) => i.id === this.highlightId);
    return highlightItem;
  }

  @computed get componentKey() {
    let value;
    try {
      value = this.highlightedFeature.value.toString();
    } catch (e) {
      value = "";
    }
    return `${this.floor}-${value}`;
  }

  @action toggleIgnoreProp(prop) {
    const ignoredProp = this.ignoredProperties.find((ignored) => ignored === prop) || null;

    if(!ignoredProp) {
      this.ignoredProperties.push(prop);
    }
  }

  @action clearItem() {
    this.currentItem = null;
    this.geoJsonRenderTime = new Date().toISOString();
  }

  constructor() {
    intercept(this, 'events', (change) => {
      if( change.newValue ) {
        // the API returns the event objects in a nested in an object - just return the events array
        change.newValue = change.newValue[0].exhibitions.filter((exhib) => ["Exhibition", "Display", "Installation"].includes(exhib.type)).filter((exhib) => exhib.site == "south-kensington");
//        change.newValue = change.newValue[0].exhibitions.filter((exhib) => ["Exhibition"].includes(exhib.type));

        change.newValue = change.newValue.map((event) => {
          // for each event, replace the THES id with the FAC0020_ prefix for exhibition spaces
          event.location_ids = event.location_ids.map((id)=>id.replace(/THES/, 'FAC0020_'));
		  // Look through all rooms and find matches
          const rooms = this.allFloorsData.filter((room) => event.location_ids.indexOf(room.id)>-1);
          event.rooms = rooms;
          return event;
        }).sort((a, b) => {
			// We want exhibitions first, then the rest;
			if(a.type == "Exhibition" && b.type != "Exhibition") {
				return -1;
			} else if(b.type == "Exhibition" && a.type != "Exhibition") {
				return 1;
			} else {
				return 0;
			}
		})
      }

      return change;
    });

    observe(this, 'currentItem', (change) => {
      if (change.newValue) {
        if( this.currentItem.hasOwnProperty('rooms') ) {
          const eventFeatures = this.geoJsonFeatures.filter((feature) => this.currentItem.rooms.map((f) => f.id).indexOf(feature.properties.id) > -1).map((feature) => toJS(feature));

          if( eventFeatures.length ) {
            const eventFeatureCollection = {"type": "FeatureCollection", "features": eventFeatures};
            const bounds = bbox(eventFeatureCollection);
            this._bounds = this.bounds;
            this.bounds = [[bounds[1], bounds[0]], [bounds[3], bounds[2]]];
          }

          if( this.currentItem.rooms.length > 0 ) {
            const floors = [... new Set(this.currentItem.rooms.map((f) => parseInt(f.level)))];
            if( floors.indexOf( this.floor ) === -1 ) {
              this.floor = floors.sort()[0];
            }
          }
        }

        if( this.currentItem.hasOwnProperty('location_ids') ) {
          this.currentItem.location_ids.map((id) => this.fadeFeature(id));
        }else if( this.currentItem.id ) {
          this.fadeFeature(this.currentItem.id);
        }
      } else {

        this.bounds = this._bounds;
        this._bounds = null;
      }
    });

    observe(this, 'highlightedFeature', (change) => {
      if (change.newValue) {

	   if(change.newValue.floors.length > 0) {

        const items = this.geoJsonFeatures.filter((f) => f.properties.type === change.newValue.slug);

        const currentFeatures = this.geoJsonFeatures.filter((feature) => this.highlightedFeature.value.indexOf(feature.properties.id) > -1).map((feature) => toJS(feature));
        const featureCollection = {"type": "FeatureCollection", "features": currentFeatures};
        const bounds = bbox(featureCollection);
        this._bounds = this.bounds;
        this.bounds = [[bounds[1], bounds[0]], [bounds[3], bounds[2]]];
	   }
      } else {
        this.trayContainerClass = false;
        this.highlightedFeature = false;

        this.bounds = this._bounds;

        this._bounds = null;
      }
    });

    observe(this, 'trailId', (change) => {
      if (change.newValue) {
        if (change.newValue == null) {
          this.trailStops = [];
          this.currentItem = null;
        } else {
          getTrail(change.newValue).then((response) => {
            this.currentItem = response.data;
            this.trailStops = response.data.stops.map((stop) => {
              const item = this.allFloorsData.find((i) => {return i.id === stop.id});
              return Object.assign({'floor': item.level}, stop)
            });
            this.trayContainerClass = 'is-flexible';
          });
        }
      }
    });

    observe(this, 'bounds', (change) => {
      if (change.newValue) {
        this.leafletRef.current.leafletElement.fitBounds(this.bounds);
      }
    });

    intercept(this, 'bounds', change => {
      if( change.newValue ) {
        change.newValue[0][0] -= 100;
        change.newValue[1][0] -= 100;
        change.newValue[1][1] += 100;
      }

      return change;
    });

    observe(this, 'highlightId', (change) => {
      if (change.newValue) {
        this.fadeFeature(change.newValue);
        const highlightItem = this.allFloorsData.find((i) => i.id === change.newValue);
        if (highlightItem) {
          this.floor = highlightItem.level;

          let events = this.events.filter((e)=> e.rooms.find((f) => f.id === change.newValue));
		  console.log(events);
          this.relatedResources = events;

          window.dataLayer.push({
              'event': 'mapRoomClick',
              'room': highlightItem.title
            }
          )
        }
      } else {
      }
    });

    observe(this, 'latlng', (change) => {
      this.position = change.newValue.split(',');
    });

    intercept(this, 'floor', change => {
      change.newValue = parseInt(change.newValue, 10);
      return change;
    });

    observe(this, 'floor', (change) => {
      if (change.newValue) {
        this.oldFloor = change.oldValue;
        this.zoom = 2;
        window.dataLayer.push({
          'event': 'virtualPageview',
          'virtualUrl': `${window.location.protocol}//${window.location.host}?floor=${change.newValue}`,
          'virtualTitle': `V&A Digital Map - Level ${change.newValue}`
        })
      }
    })
  }

  updateStateUsingLocation(location, routingStore) {
    this.mapToolsOpen = /^\/(find|where-am-i|trails)\/?/.test(location.pathname);

    let params = new URLSearchParams(location.search);
    params.forEach((value, key) => {
      const ignored = this.ignoredProperties.find((prop) => prop === key);

      if (isObservableProp(this, key) && !ignored) {
        this[key] = value;
      }
    });

    return this;
  }

  fadeFeature(feature_id) {
    if( !this.leafletRef) return ;

    const leafletFeatures = Object.values(this.leafletRef.current.leafletElement._layers).filter((l) => l.feature ? l : false);
    const feature = leafletFeatures.find((l) => l.feature.properties.id === feature_id);
    if( feature ) {
      feature._path.classList.remove("do-fade");
      setTimeout(() => feature._path.classList.add("do-fade"), 25);
    }
  }

  static fromLocation(location) {
    let store = new VandAMapStore();
    store.updateStateUsingLocation(location);

    return store;
  }
}

export default VandAMapStore;
